import Axios from "axios"

import { ABLE_TO_DO_ROLS, PRODUCTS_TABS, TABLE_TAB_HEADER, PO_STATUS, generateOrderPdf } from "../warehousesTab/utils"
import { useEffect, useMemo, useState } from "react"
import { convertDate } from "../documentCompliance/utils";
import { useAuth } from "../../../context/auth-user";
import { Dropdown, Modal } from "react-bootstrap";

import sortIcon from "../../../assets/images/sort.svg"
import dots from "../../../assets/images/moreDots.svg"
import loadingGif from "../../../assets/images/loading.gif"
import close from "../../../assets/images/close.svg"
import { formatCommas } from "../../../utilities/formatCommas";

const {
    REACT_APP_PURCHASING_LOCALHOST
} = process.env

export default function PurchaseOrders({ activeMainTab, supplier }) {
    const { user } = useAuth()
    const userLogged = useMemo(() => user?.selectedProfile, [user?.selectedProfile]);

    const [purchaseOrders, setPurchaseOrders] = useState([])

    const [sort, setSort] = useState("ASC")
    const [sortType, setSortType] = useState("locationName")
    const [loading, setLoading] = useState(false)

    const [showMoreOptions, setShowMoreOptions] = useState("")
    const [isStatusModalActive, setIsStatusModalActive] = useState(false);
    const [modalInfo, setModalInfo] = useState(null)
    const [showModal, setShowModal] = useState(false)
    const [commentText, setCommentText] = useState('');

    const [selectedWarehouse, setSelectedWarehouse] = useState(null)

    useEffect(() => {
        function closeMoreOptions(e) {
            if (e.target.className !== "more-dots") {
                setShowMoreOptions(null)
            }
        }

        document.body.addEventListener("click", closeMoreOptions)

        return function cleanUp() {
            document.body.removeEventListener("click", closeMoreOptions)
        }
    }, [])

    useEffect(() => {
        if (activeMainTab !== "Purchase Orders" || !selectedWarehouse) return

        const controller = new AbortController();
        const signal = controller.signal;

        let isMounted = true; // Track if the component is still mounted

        setLoading(true)

        Axios.get(`${REACT_APP_PURCHASING_LOCALHOST}order/supplier/${supplier._id}/warehouse/${selectedWarehouse?._id}/getAllBySupplierAndWarehouse`, { signal })
            .then(res => {
                if (isMounted) {
                    setPurchaseOrders(res.data?.orders);
                }
            })
            .catch(err => {
                if (!Axios.isCancel(err)) {
                    console.log(err);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            })

        return () => {
            isMounted = false; // Mark as unmounted
            controller.abort(); // Cleanup on unmount or dependency change
        };
    }, [selectedWarehouse, supplier._id, activeMainTab]);

    const handleSortItems = (property) => {
        setSort((prevSort) => (sortType === property ? (prevSort === "ASC" ? "DSC" : "ASC") : "ASC"));
        setSortType(property);
    }

    const handleOrderModal = (order) => {
        setModalInfo(order)
        setShowModal(true)
    }

    const handleMoreOptions = (folio) => {
        if (folio === showMoreOptions) {
            setShowMoreOptions("1234")

            return
        }

        setShowMoreOptions(folio)
    }

    const handleUpdateOrderStatus = (order, status) => {
        if (order.status === "Order completed" || order.status === "Canceled") return

        const dataToSend = {
            status
        }

        Axios.put(`${REACT_APP_PURCHASING_LOCALHOST}order/${order._id}/updateStatus`, dataToSend).then(res => {
            const itemsCopy = [...purchaseOrders]

            const orderUpdated = itemsCopy.find(item => item._id === res.data?.order?._id)
            if (orderUpdated) {
                orderUpdated.status = res.data.order.status
            }

            setPurchaseOrders(itemsCopy)

            if (showModal) {
                setModalInfo(prev => {
                    return {
                        ...prev,
                        status: res.data.order.status
                    }
                })
            }
        }).catch(err => console.log(err))
    }

    const handleSelectWarehouse = (wh) => {
        if (wh._id === selectedWarehouse?._id) {
            setSelectedWarehouse(null)

            return
        }

        setSelectedWarehouse(wh)
    }

    const handleDownloadPO = async () => {
        try {
            await generateOrderPdf(modalInfo);
        } catch (error) {
            console.error('Failed to download PDF:', error);
        }
    };

    const handleTextareaChange = (e) => {
        setCommentText(e.target.value);
    };

    const sendComment = () => {
        const orderNote = commentText;
        const username = modalInfo?.supplier?.name;
        Axios.put(`${REACT_APP_PURCHASING_LOCALHOST}order/${modalInfo?._id}/updateOrderNote`, {
            orderNote,
            username,
            sender: "Supplier"
        }).then(res => {
            setModalInfo(prev => {
                const newComments = prev.orderNote;
                newComments.push({
                    text: orderNote,
                    username,
                });
                return { ...prev, orderNote: newComments };
            });
        }).catch(err => {
            console.log(err)
        })

        setCommentText('');
    }

    const ableToDo = ABLE_TO_DO_ROLS.includes(userLogged?.rol)

    const address = useMemo(() => `${modalInfo?.address} ${modalInfo?.zipCode} ${modalInfo?.county}, ${modalInfo?.state}`, [modalInfo])
    const orderStatusClass = `order-status ${modalInfo?.status === "Canceled" || modalInfo?.status === "Order in dispute" ? "red" : (modalInfo?.status !== "Order completed" ? "orange" : "")}`

    return (
        <section className="prodAsign-table">
            <div className="warehouse-dropdown mb-3">
                <Dropdown>
                    <Dropdown.Toggle>
                        {selectedWarehouse?.name || "Select warehouse"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        {
                            supplier?.warehouses?.map(wh => {
                                return (
                                    <Dropdown.Item
                                        key={wh._id}
                                        onClick={() => handleSelectWarehouse(wh)}
                                    >
                                        {wh?.name}
                                    </Dropdown.Item>
                                )
                            })
                        }
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className={`table-header ${PRODUCTS_TABS[1]} d-none d-lg-grid`}>
                {TABLE_TAB_HEADER["Purchase orders"]?.map(item => {
                    return (
                        <div key={`${PRODUCTS_TABS[1]}-${item.property}`} className="header-item-container">
                            <span className="header-item">{item.title}</span>
                            <img src={sortIcon} alt="Prodensa Supplier" onClick={() => handleSortItems(item.property)} />
                        </div>
                    )
                })}
            </div>

            <div className={`table-section ${PRODUCTS_TABS[1]}`}>
                {!selectedWarehouse &&
                    <div className="no-data d-flex justify-content-center py-5">
                        <h4>Select warehouse</h4>
                    </div>
                }

                {selectedWarehouse && loading &&
                    <div className="loading d-flex justify-content-center py-5">
                        <img
                            style={{ width: "45px", height: "45px" }}
                            src={loadingGif}
                            alt="Prodensa Supplier"
                        />
                    </div>
                }

                {selectedWarehouse && purchaseOrders.length === 0 && !loading &&
                    <div className="no-data d-flex justify-content-center py-5">
                        <h4>No purchase orders found</h4>
                    </div>
                }

                {selectedWarehouse && purchaseOrders.length > 0 && !loading &&
                    purchaseOrders.sort((a, b) => {
                        const itemInfoA = a
                        const itemInfoB = b

                        if (sort === "ASC") {
                            return itemInfoA?.[sortType]?.localeCompare(itemInfoB?.[sortType])
                        } else {
                            return itemInfoB?.[sortType]?.localeCompare(itemInfoA?.[sortType])
                        }
                    })
                    .map(item => {
                        const itemInfo = item

                        const element1Class = `element-1 withCursor`
                        const element2Info = convertDate(itemInfo?.[TABLE_TAB_HEADER?.[PRODUCTS_TABS[1]]?.[3]?.property])
                        const moreOptionsClass = `more-options ${showMoreOptions === itemInfo?.folio ? "show" : ""}`
                        const elementPOClass = `element-po ${itemInfo?.status !== "Order completed" &&
                            itemInfo?.status !== "Canceled" &&
                            itemInfo?.status !== "Order in dispute" ? "orange" :
                            (itemInfo?.status === "Canceled" || itemInfo?.status === "Order in dispute" ? "red" : "")}`

                        const enableMoreOptions = itemInfo?.status !== "Order completed" && itemInfo?.status !== "Canceled"
                        const moreOptionsContainerClass = `more-options-container ${enableMoreOptions ? "" : "not-allowed"}`

                        return (
                            <div className={`table-element ${PRODUCTS_TABS[1]}`} key={itemInfo?.[TABLE_TAB_HEADER?.[PRODUCTS_TABS[1]]?.[0]?.property]}>
                                <span className={element1Class} onClick={() => handleOrderModal(item)}>{itemInfo?.[TABLE_TAB_HEADER?.[PRODUCTS_TABS[1]]?.[0]?.property]}</span>

                                <span className={`element-header ${element1Class}`} onClick={() => handleOrderModal(item)}>
                                    {itemInfo?.[TABLE_TAB_HEADER?.[PRODUCTS_TABS[1]]?.[1]?.property]}
                                </span>

                                <span className={`element-variable ${PRODUCTS_TABS[1]} ${element1Class}`} onClick={() => handleOrderModal(item)}>
                                    {itemInfo?.[TABLE_TAB_HEADER?.[PRODUCTS_TABS[1]]?.[2]?.property]}
                                </span>

                                <span className={`element-2 ${element1Class}`} onClick={() => handleOrderModal(item)}>
                                    {element2Info}
                                </span>

                                <span className={elementPOClass}>
                                    {itemInfo?.[TABLE_TAB_HEADER?.[PRODUCTS_TABS[1]]?.[4]?.property]}
                                </span>

                                {ableToDo &&
                                    <div className={moreOptionsContainerClass} onClick={() => enableMoreOptions && handleMoreOptions(itemInfo?.folio)}>
                                        <img className="more-dots" src={dots} alt="Prodensa Supplier" />
                                        <div className={moreOptionsClass}>
                                            <span onClick={() => handleUpdateOrderStatus(itemInfo, PO_STATUS.inPreparation)}>In preparation</span>
                                            <span onClick={() => handleUpdateOrderStatus(itemInfo, PO_STATUS.sent)}>Sent</span>
                                            <span onClick={() => handleUpdateOrderStatus(itemInfo, PO_STATUS.delivered)}>Delivered</span>
                                        </div>
                                    </div>
                                }
                            </div>
                        )
                    })
                }
            </div>

            {/* Modal for purchase order */}
            <Modal className={`document-modal po-modal ${userLogged?.darkLightMode ? "darkMode" : ""}`} centered show={showModal} onHide={() => {
                setShowModal(false)
                setIsStatusModalActive(false);
            }} onClick={() => setIsStatusModalActive(false)}>
                <img className="closeModal" src={close} alt="Catenna Platform" onClick={() => {
                    setShowModal(false)
                    setIsStatusModalActive(false);
                }} />

                <Modal.Header>
                    <Modal.Title>
                        <h4>Purchase order #{modalInfo?.folio}</h4>
                    </Modal.Title>
                    <div className="order-top-options">
                        <div className="order-status-container no-select" onClick={(e) => {
                            e.stopPropagation();
                            setIsStatusModalActive(prev => !prev)
                        }}>
                            <span className={orderStatusClass}>{modalInfo?.status}</span>
                            <div>
                                <span className="status-button-icon"></span>
                            </div>
                            {isStatusModalActive &&
                                <div className="status-options-modal">
                                    <span className="status-option" onClick={() => handleUpdateOrderStatus(modalInfo, PO_STATUS.inPreparation)}>In preparation</span>
                                    <span className="status-option" onClick={() => handleUpdateOrderStatus(modalInfo, PO_STATUS.sent)}>Sent</span>
                                    <span className="status-option" onClick={() => handleUpdateOrderStatus(modalInfo, PO_STATUS.delivered)}>Delivered</span>
                                </div>
                            }
                        </div>
                        <button className="download-po-button" onClick={() => handleDownloadPO(modalInfo?._id)}>
                            <span className="download-po-button-text">Download P.O.</span>
                            <span className="download-po-button-icon"></span>
                        </button>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <div className="order-information-main-container">
                        <div className="order-information-subcontainer">
                            <div className="order-information-subcontainer-items">
                                <h5>Supplier</h5>
                                <div className="order-information-container" >
                                    <span className="rfc">{modalInfo?.supplier?.rfc}</span>
                                    <span className="supplier-name">{modalInfo?.supplier?.name}</span>
                                </div>
                            </div>
                            <div className="order-information-subcontainer-items">
                                <h5>Client</h5>
                                <div className="order-information-container">
                                    <span className="location">{modalInfo?.locationName}</span>
                                    <span className="address">{address}</span>
                                </div>
                            </div>
                        </div>
                        <div className="order-information-subcontainer">
                            <div className="product-list-container">
                                <div className="product-list-header">
                                    <h5 className="product-list-header-item">Barcode</h5>
                                    <h5 className="product-list-header-item">Product</h5>
                                    <h5 className="product-list-header-item">Qty</h5>
                                    <h5 className="product-list-header-item">Unit price</h5>
                                    <h5 className="product-list-header-item">Total</h5>
                                </div>
                                <div className="product-list">
                                    {modalInfo?.products.map(prod => {
                                        return (
                                            <div key={prod._id} className="product">
                                                <div className="product-item-wrapper barcodeItem">
                                                    <span className="product-item">{prod.specificProduct?.barcode}</span>
                                                    <div className="product-label" data-label="Barcode"></div>
                                                </div>
                                                <div className="product-item-wrapper productNameItem">
                                                    <h5 className="product-item productNameItem">{prod.specificProduct?.name}</h5>
                                                    <div className="product-label" data-label="Product"></div>
                                                </div>
                                                <div className="product-item-wrapper">
                                                    <span className="product-item">{prod.quantity}</span>
                                                    <div className="product-label" data-label="Qty."></div>
                                                </div>
                                                <div className="product-item-wrapper">
                                                    <span className="product-item">${prod.specificProduct?.price && formatCommas(prod.specificProduct?.price).formattedValue}</span>
                                                    <div className="product-label" data-label="Unit price"></div>
                                                </div>
                                                <div className="product-item-wrapper">
                                                    <span className="product-price product-item">${prod.totalProductPrice && formatCommas(prod.totalProductPrice).formattedValue}</span>
                                                    <div className="product-label" data-label="Total"></div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>

                                <div className="total-cost">
                                    <span>Total order price:</span>
                                    <span>${modalInfo?.totalOrderPrice && formatCommas(modalInfo?.totalOrderPrice).formattedValue}</span>
                                </div>
                            </div>
                        </div>
                        <span className="comments-label">Comments</span>
                        <div className="order-information-subcontainer">
                            <div className="comments-container">
                                {modalInfo?.orderNote?.slice().reverse().map((comment) =>
                                    <div className="order-information-container">
                                        <span className="comment">{`${comment.username} : ${comment.text}`}</span>
                                    </div>
                                )}
                            </div>
                            <div className="add-comment-container">
                                <textarea className="add-comment-textarea" placeholder="Write a comment..." value={commentText} onChange={handleTextareaChange}></textarea>
                                <button className="add-comment-button" onClick={() => sendComment()}>
                                    <span className="add-comment-button-text">
                                        Send comment
                                    </span>
                                </button>
                            </div>
                            <button className="download-po-button-mobile" onClick={() => handleDownloadPO(modalInfo?._id)}>
                                <span className="download-po-button-text">Download P.O.</span>
                                <span className="download-po-button-icon"></span>
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </section>
    )
}
